/**
 * @fileoverview CategoryTable component displays categories with responsive layouts.
 * Shows a table on desktop and card layout on mobile for better mobile usability.
 */

import { formatDateForDisplay } from '../../utils/dateUtils';
import { formatCurrency } from '../../utils/moneyUtils';
import styles from './CategoryTable.module.css';

/**
 * CategoryTable Component
 *
 * Displays existing categories. Shows as a table on desktop and card layout on mobile.
 */
function CategoryTable({
    categories,
    canManageCategories,
    isActive,
    isFutureDated,
    handleCeaseCategory,
    handleReactivateCategory,
    handleDeleteCategory,
    handleEditCategory,
    readOnly = false
}) {
    const renderActionButtons = (category) => {
        // Hide all action buttons when in read-only mode
        if (readOnly) {
            return null;
        }
        if (category.userKey !== null && category.userKey !== undefined) {
            // For ceased user categories, show as ceased
            if (!isActive(category)) {
                return (
                    <span className={styles.userCategory}>
                        User Category (Ceased)
                    </span>
                );
            }
            return (
                <span className={styles.userCategory}>
                    User Category
                </span>
            );
        }

        if (isFutureDated(category)) {
            return (
                <button
                    className={styles.deleteButton}
                    onClick={() => handleDeleteCategory(category.categoryKey, category.categoryName)}
                >
                    Delete
                </button>
            );
        }

        if (isActive(category)) {
            return (
                <div className={styles.buttonGroup}>
                    <button
                        className={styles.editButton}
                        onClick={() => handleEditCategory(category)}
                    >
                        Edit
                    </button>
                    <button
                        className={styles.ceaseButton}
                        onClick={() => handleCeaseCategory(category.categoryKey, category.categoryName)}
                    >
                        Cease
                    </button>
                </div>
            );
        }

        return (
            <button
                className={styles.reactivateButton}
                onClick={() => handleReactivateCategory(category.categoryKey)}
            >
                Reactivate
            </button>
        );
    };

    return (
        <div className={styles.section}>
            <h4 className={styles.heading}>Existing Categories</h4>

            {/* Desktop Table View */}
            <div className={styles.tableWrapper}>
                <table className={styles.table}>
                    <thead>
                        <tr>
                            <th>Category Name</th>
                            <th>Sort Order</th>
                            <th>Expense</th>
                            <th>Transfer</th>
                            <th>Start Date</th>
                            <th>End Date</th>
                            <th>Starting Balance</th>
                            <th>Status</th>
                            {canManageCategories && (
                                <th>Actions</th>
                            )}
                        </tr>
                    </thead>
                    <tbody>
                        {categories.map(category => (
                            <tr key={category.categoryKey}>
                                <td>
                                    {category.categoryName}
                                </td>
                                <td>
                                    {category.sortOrder || 0}
                                </td>
                                <td className={styles.centered}>
                                    {category.allowExpense ? '✓' : ''}
                                </td>
                                <td className={styles.centered}>
                                    {category.allowTransfer ? '✓' : ''}
                                </td>
                                <td>
                                    {formatDateForDisplay(category.startDate)}
                                </td>
                                <td>
                                    {formatDateForDisplay(category.endDate)}
                                </td>
                                <td>
                                    {category.startingBalance && parseFloat(category.startingBalance) !== 0
                                        ? formatCurrency(parseFloat(category.startingBalance))
                                        : '-'
                                    }
                                </td>
                                <td>
                                    <span className={`${styles.statusBadge} ${isActive(category) ? styles.statusActive : styles.statusCeased}`}>
                                        {isActive(category) ? 'Active' : 'Ceased'}
                                    </span>
                                </td>
                                {canManageCategories && (
                                    <td>
                                        {renderActionButtons(category)}
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Mobile Card View */}
            <div className={styles.cardContainer}>
                {categories.map(category => (
                    <div key={category.categoryKey} className={styles.card}>
                        <div className={styles.cardHeader}>
                            <h5 className={styles.cardTitle}>{category.categoryName}</h5>
                            <span className={`${styles.statusBadge} ${isActive(category) ? styles.statusActive : styles.statusCeased}`}>
                                {isActive(category) ? 'Active' : 'Ceased'}
                            </span>
                        </div>
                        <div className={styles.cardBody}>
                            <div className={styles.cardRow}>
                                <span className={styles.cardLabel}>Sort Order:</span>
                                <span className={styles.cardValue}>{category.sortOrder || 0}</span>
                            </div>
                            <div className={styles.cardRow}>
                                <span className={styles.cardLabel}>Expense:</span>
                                <span className={styles.cardValue}>{category.allowExpense ? '✓' : ''}</span>
                            </div>
                            <div className={styles.cardRow}>
                                <span className={styles.cardLabel}>Transfer:</span>
                                <span className={styles.cardValue}>{category.allowTransfer ? '✓' : ''}</span>
                            </div>
                            <div className={styles.cardRow}>
                                <span className={styles.cardLabel}>Start Date:</span>
                                <span className={styles.cardValue}>{formatDateForDisplay(category.startDate)}</span>
                            </div>
                            <div className={styles.cardRow}>
                                <span className={styles.cardLabel}>End Date:</span>
                                <span className={styles.cardValue}>{formatDateForDisplay(category.endDate)}</span>
                            </div>
                            <div className={styles.cardRow}>
                                <span className={styles.cardLabel}>Starting Balance:</span>
                                <span className={styles.cardValue}>
                                    {category.startingBalance && parseFloat(category.startingBalance) !== 0
                                        ? formatCurrency(parseFloat(category.startingBalance))
                                        : '-'
                                    }
                                </span>
                            </div>
                        </div>
                        {canManageCategories && (
                            <div className={styles.cardActions}>
                                {renderActionButtons(category)}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

export default CategoryTable;
